import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { toast } from 'react-toastify'

import instagramPlatformLogo from '../../assets/images/platforms/platform-instagram.png'
import youtubePlatformLogo from '../../assets/images/platforms/platform-youtube.png'
import { RootState } from 'store'
import { getErrorMessage } from 'utils/helpers'
import { bulkAddContacts } from 'services/api/creators'
import {
    BulkAddContactsPayload,
    BulkContactItem,
    BulkUploadColumnField,
} from 'types/creators'
import {
    BULK_UPLOAD_ACCEPTED_FILE_TYPES,
    BULK_UPLOAD_SAMPLE_CSV,
    downloadSampleCsv,
    parseSpreadsheetFile,
} from 'utils/csvHelpers'
import './BulkUploadCreatorContactModal.scss'

const platforms = [
    { id: 'ig', name: 'Instagram' },
    { id: 'yt', name: 'YouTube' },
]

const platformLogos: Record<string, string> = {
    ig: instagramPlatformLogo,
    yt: youtubePlatformLogo,
}

const COLUMN_FIELD_OPTIONS: { value: BulkUploadColumnField; label: string }[] = [
    { value: 'skip', label: 'Ignore' },
    { value: 'handle', label: 'Handle' },
    { value: 'tags', label: 'Tags' },
]

const inferColumnField = (header: string): BulkUploadColumnField => {
    const normalized = header.trim().toLowerCase()
    if (normalized === 'handle' || normalized === 'username') return 'handle'
    if (normalized === 'tags' || normalized === 'tag') return 'tags'
    return 'skip'
}

const parseTagsCell = (value: string): string[] => {
    if (!value.trim()) return []
    return value
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean)
}

type BulkUploadCreatorContactModalProps = {
    isOpen: boolean
    onClose: () => void
    onUploadSuccess: () => void
}

const BulkUploadCreatorContactModal: React.FC<BulkUploadCreatorContactModalProps> = ({
    isOpen,
    onClose,
    onUploadSuccess,
}) => {
    const selectedHubId = useSelector((state: RootState) => state.auth.selectedHubId)

    const [processing, setProcessing] = useState(false)
    const [platform, setPlatform] = useState('ig')
    const [fileName, setFileName] = useState('')
    const [headers, setHeaders] = useState<string[]>([])
    const [rows, setRows] = useState<string[][]>([])
    const [columnMapping, setColumnMapping] = useState<BulkUploadColumnField[]>([])
    const [platformError, setPlatformError] = useState<string>()
    const [mappingError, setMappingError] = useState<string>()

    const resetForm = () => {
        setPlatform('ig')
        setFileName('')
        setHeaders([])
        setRows([])
        setColumnMapping([])
        setPlatformError(undefined)
        setMappingError(undefined)
    }

    useEffect(() => {
        if (isOpen) {
            resetForm()
        }
    }, [isOpen])

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        event.target.value = ''
        if (!file) return

        try {
            const parsed = await parseSpreadsheetFile(file)
            if (parsed.length === 0) {
                toast.error('The file is empty.')
                return
            }

            const [headerRow, ...dataRows] = parsed
            const inferredMapping = headerRow.map(inferColumnField)
            const hasHeaderMapping = inferredMapping.some((field) => field !== 'skip')
            const resolvedHeaders = hasHeaderMapping
                ? headerRow
                : headerRow.map((_, index) => `Column ${index + 1}`)
            const resolvedRows = hasHeaderMapping ? dataRows : parsed

            setFileName(file.name)
            setHeaders(resolvedHeaders)
            setRows(resolvedRows.filter((row) => row.some((cell) => cell.trim())))
            setColumnMapping(
                hasHeaderMapping
                    ? inferredMapping
                    : headerRow.map((_, index) => (index === 0 ? 'handle' : index === 1 ? 'tags' : 'skip'))
            )
            setMappingError(undefined)
        } catch (error: unknown) {
            toast.error(
                getErrorMessage(error) || 'Failed to read the file. Please upload a valid CSV or XLSX file.'
            )
        }
    }

    const mappedContacts = useMemo((): BulkContactItem[] => {
        if (!rows.length || !columnMapping.length) return []

        const handleIndex = columnMapping.indexOf('handle')
        const tagsIndex = columnMapping.indexOf('tags')
        if (handleIndex === -1) return []

        return rows
            .map((row) => {
                const handle = row[handleIndex]?.trim()
                if (!handle) return null
                const tags = tagsIndex >= 0 ? parseTagsCell(row[tagsIndex] ?? '') : []
                return { handle, tags }
            })
            .filter((contact): contact is BulkContactItem => contact !== null)
    }, [rows, columnMapping])

    const validateForm = () => {
        let valid = true
        if (!platform) {
            setPlatformError('Please select a social media platform.')
            valid = false
        } else {
            setPlatformError(undefined)
        }

        if (!rows.length) {
            setMappingError('Please upload a CSV or XLSX file with at least one row.')
            valid = false
        } else if (!columnMapping.includes('handle')) {
            setMappingError('Please map one column to Handle.')
            valid = false
        } else if (mappedContacts.length === 0) {
            setMappingError('No valid rows found. Ensure the Handle column has values.')
            valid = false
        } else {
            setMappingError(undefined)
        }

        return valid
    }

    const handleSubmit = async () => {
        if (!validateForm() || !selectedHubId) return

        const payload: BulkAddContactsPayload = {
            platform,
            contacts: mappedContacts,
        }

        try {
            setProcessing(true)
            await bulkAddContacts(selectedHubId, payload)
            toast.success(`${mappedContacts.length} creator${mappedContacts.length === 1 ? '' : 's'} queued for import.`)
            onUploadSuccess()
            onClose()
        } catch (error: unknown) {
            console.error(error)
            toast.error(
                getErrorMessage(error) || 'Failed to upload creators. Please try again.'
            )
        } finally {
            setProcessing(false)
        }
    }

    const updateColumnMapping = (index: number, field: BulkUploadColumnField) => {
        setColumnMapping((prev) => {
            const next = [...prev]
            if (field !== 'skip') {
                const existingIndex = next.indexOf(field)
                if (existingIndex >= 0 && existingIndex !== index) {
                    next[existingIndex] = 'skip'
                }
            }
            next[index] = field
            return next
        })
        setMappingError(undefined)
    }

    return (
        <Modal
            isOpen={isOpen}
            toggle={onClose}
            centered
            className="bulk-upload-creator-contact-modal"
        >
            <ModalHeader toggle={onClose}>Bulk Upload Creators</ModalHeader>
            <ModalBody>
                <p className="modal-subtitle">
                    Upload a CSV or XLSX file with creator handles and optional tags. All contacts will be added to the selected platform.
                </p>

                <div className="bulk-upload-form">
                    <div className="mb-3">
                        <label className="form-label">
                            Platform <span className="text-danger">*</span>
                        </label>
                        <div className="platform-picker" role="radiogroup" aria-label="Platform">
                            {platforms.map((item) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={item.id === platform}
                                    className={`platform-option ${item.id === platform ? 'active' : ''}`}
                                    onClick={() => {
                                        setPlatform(item.id)
                                        setPlatformError(undefined)
                                    }}
                                >
                                    <img
                                        className="platform-option__icon"
                                        src={platformLogos[item.id]}
                                        alt={item.name}
                                    />
                                </button>
                            ))}
                        </div>
                        {platformError && (
                            <div className="text-danger field-error">{platformError}</div>
                        )}
                    </div>

                    <div className="mb-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
                        <label className="form-label mb-0">
                            File <span className="text-danger">*</span>
                        </label>
                        <button
                            type="button"
                            className="theme-btn btn-primary-outlined btn-sm bulk-upload-sample-btn"
                            onClick={() => downloadSampleCsv('creators-bulk-upload-sample.csv', BULK_UPLOAD_SAMPLE_CSV)}
                        >
                            <i className="ri-download-2-line me-1" />
                            Download Sample CSV
                        </button>
                    </div>

                    <div className="bulk-upload-file-input mb-3">
                        <input
                            id="bulk-upload-file"
                            type="file"
                            accept={BULK_UPLOAD_ACCEPTED_FILE_TYPES}
                            className="form-control"
                            onChange={handleFileChange}
                        />
                        {fileName && (
                            <p className="bulk-upload-file-name text-muted mb-0 mt-2">
                                <i className="ri-file-text-line me-1" />
                                {fileName} · {rows.length} row{rows.length === 1 ? '' : 's'}
                            </p>
                        )}
                    </div>

                    {headers.length > 0 && (
                        <div className="bulk-upload-mapping mb-0">
                            <label className="form-label">Column Mapping</label>
                            <p className="text-muted fs-12 mb-2">
                                Match each sheet column to the corresponding field.
                            </p>
                            <div className="bulk-upload-mapping-grid">
                                {headers.map((header, index) => (
                                    <div key={`${header}-${index}`} className="bulk-upload-mapping-row">
                                        <span className="bulk-upload-mapping-row__header" title={header}>
                                            {header}
                                        </span>
                                        <select
                                            className="form-select form-select-sm"
                                            value={columnMapping[index] ?? 'skip'}
                                            onChange={(e) =>
                                                updateColumnMapping(index, e.target.value as BulkUploadColumnField)
                                            }
                                        >
                                            {COLUMN_FIELD_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                            </div>
                            {mappedContacts.length > 0 && (
                                <p className="text-muted fs-12 mt-2 mb-0">
                                    {mappedContacts.length} contact{mappedContacts.length === 1 ? '' : 's'} ready to upload.
                                </p>
                            )}
                        </div>
                    )}

                    {mappingError && (
                        <div className="text-danger field-error mt-2">{mappingError}</div>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <button type="button" className="theme-btn btn-primary-outlined" onClick={onClose}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="theme-btn btn-primary"
                    onClick={handleSubmit}
                    disabled={processing || !rows.length}
                >
                    {processing ? 'Uploading...' : 'Upload Creators'}
                </button>
            </ModalFooter>
        </Modal>
    )
}

export default BulkUploadCreatorContactModal
